import { createContext, useState } from "react";
import { isPojo } from "../../utils/object";
import { text } from "../../utils/i18n";
import { applyUpdate } from "../../utils";
import { withShift } from "../../utils/keyboard";

export type KeyboardShortcuts = {
  //  data-ks 7 id => key
  "call-hang-up": string,
  "call-mute": string,
  "call-big-view": string,
  "call-0": string,
  "call-1": string,
  "call-2": string,
  "call-3": string,
  "call-4": string,
  "call-5": string,
  "call-6": string,
  "call-7": string,
  "call-8": string,
  "call-9": string,
};

export type KeyboardShortcutSettings = {
  isIndicatorEnabled: boolean;
  isEnabled: boolean;
  actions: KeyboardShortcuts;
}

export const defaultKeyboardSettings: KeyboardShortcutSettings = {
  isIndicatorEnabled: true,
  isEnabled: false,
  actions: {
    "call-hang-up": "-",
    "call-mute": "m",
    "call-big-view": withShift("q"),
    "call-0": "1",
    "call-1": "2",
    "call-2": "3",
    "call-3": "4",
    "call-4": "5",
    "call-5": "6",
    "call-6": "7",
    "call-7": "8",
    "call-8": "9",
    "call-9": "0",
  }
};

export function getKeyboardShortcutNames(): { [k in keyof KeyboardShortcuts]: { name: string, icon: string; } } {
  //  Se defaultKeyboardShortcuts ovanför.
  return {
    "call-hang-up": { name: text`Lägg på samtal`, icon: "phone-slash" },
    "call-mute": { name: text`Mikrofon på/av`, icon: "microphone-slash" },
    "call-big-view": { name: text`Gör större`, icon: "expand" },
    "call-0": { name: text`Tagg / verktyg ${1}`, icon: "wrench" },
    "call-1": { name: text`Tagg / verktyg ${2}`, icon: "wrench" },
    "call-2": { name: text`Tagg / verktyg ${3}`, icon: "wrench" },
    "call-3": { name: text`Tagg / verktyg ${4}`, icon: "wrench" },
    "call-4": { name: text`Tagg / verktyg ${5}`, icon: "wrench" },
    "call-5": { name: text`Tagg / verktyg ${6}`, icon: "wrench" },
    "call-6": { name: text`Tagg / verktyg ${7}`, icon: "wrench" },
    "call-7": { name: text`Tagg / verktyg ${8}`, icon: "wrench" },
    "call-8": { name: text`Tagg / verktyg ${9}`, icon: "wrench" },
    "call-9": { name: text`Tagg / verktyg ${10}`, icon: "wrench" },
  };
}

function getSettings(): KeyboardShortcutSettings {
  try {
    const string = window.localStorage.getItem("keyboardShortcuts");
    if (!string) {
      return defaultKeyboardSettings;
    }

    const obj: KeyboardShortcutSettings = JSON.parse(string);
    if (!isPojo(obj)) {
      return defaultKeyboardSettings;
    }

    for (const k in defaultKeyboardSettings) {
      obj[k] = obj[k] || defaultKeyboardSettings[k];
    }

    for (const id in defaultKeyboardSettings.actions) {
      obj.actions[id] = obj.actions[id] || defaultKeyboardSettings.actions[id];
    }

    //  Ta bort dubbletter så man inte kan råka göra två saker med samma tangent.
    const used: any = {};
    for (const id in obj.actions) {
      if (used[id]) {
        obj.actions[id] = null;
      }

      used[id] = true;
    }

    return obj;

  } catch (error) {
    console.warn("Error getting keyboard shortcuts", error);
  }
}

export const KeyboardShortcutContextProvider: React.FC = ({ children }) => {
  const [settings, setSettings] = useState(getSettings());
  const [nonce, setNonce] = useState(0);

  return (
    <KeyboardShortcutContext.Provider value={{
      settings,
      _nonce: nonce,
      updateSettings(update) {
        setSettings(settings => {
          const updated = applyUpdate(update, settings);

          window.localStorage.setItem("keyboardShortcuts", JSON.stringify(updated));

          return updated;
        })
      },
      updateIndicatorsInDom: () => {
        if (!settings.isIndicatorEnabled) {
          return;
        }
        
        setNonce(Date.now());
      }
    }}>
      {children}
    </KeyboardShortcutContext.Provider>
  )
}


export const KeyboardShortcutContext = createContext<{
  updateSettings: (update: Update<KeyboardShortcutSettings>) => void;
  settings: KeyboardShortcutSettings;

  /**
   * Indicators will be updated on DOM when this value changes.
   */
  _nonce: number;

  /**
   * Updates keyboard shortcut indicators in DOM.
   * Call this function when making som state change which can reveal KS indicators.
   */
  updateIndicatorsInDom: () => void;
}>(null);